import { z } from 'zod';
import { AccountSettingSchema } from './account-settings.dto';
import { inferApiResultSchema } from './infer-api';

export const defaultPortalId = 0;
export const portalNameLimit = 300;

export const PortalSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  name: z.string().trim().min(1).max(portalNameLimit),
  portalSlug: z.string().min(8).max(portalNameLimit),
  loginLogoKey: z.string().nullish(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format'),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format'),
  faviconKey: z.string().nullish(),
  createdBy: z.string().max(50),
  createdAt: z.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  deletedAt: z.coerce.date().nullish(),
  publishedFeedbackFormCount: z.number().optional(),
  loginLogoUrl: z.string().max(2083).nullish(),
  faviconUrl: z.string().max(2083).nullish(),
  domainFilteringEnabled: z.boolean().default(false),
  filteredDomains: z.array(z.string()).optional(),
  includeInLandingPage: z.boolean().nullish(),
});

export type PortalDto = z.infer<typeof PortalSchema>;

export const GetPortalResponseSchema = inferApiResultSchema(PortalSchema);
export type GetPortalResponseDto = z.infer<typeof GetPortalResponseSchema>;

export const GetPortalsResponseSchema = inferApiResultSchema(z.array(PortalSchema));
export type GetPortalsResponseDto = z.infer<typeof GetPortalsResponseSchema>;

export const CreatePortalSchema = PortalSchema.omit({
  id: true,
  accountId: true,
  createdBy: true,
  createdAt: true,
  archivedAt: true,
  deletedAt: true,
  domainFilteringEnabled: true,
  filteredDomains: true,
});

export type CreatePortalDto = z.infer<typeof CreatePortalSchema>;

export const CreatePortalResponseSchema = inferApiResultSchema(PortalSchema);
export type CreatePortalResponseDto = z.infer<typeof CreatePortalResponseSchema>;

export const UpdatePortalSchema = PortalSchema.omit({
  id: true,
  accountId: true,
  createdBy: true,
  createdAt: true,
  archivedAt: true,
  deletedAt: true,
  domainFilteringEnabled: true,
  filteredDomains: true,
}).partial();

export type UpdatePortalDto = z.infer<typeof UpdatePortalSchema>;

export const UpdatePortalResponseSchema = inferApiResultSchema(PortalSchema);
export type UpdatePortalResponseDto = z.infer<typeof UpdatePortalResponseSchema>;

export const PortalSettingsSchema = z.object({
  theme: z.object({
    accountId: z.string().max(50),
    portalSlug: z.string().min(8).max(portalNameLimit).nullish(),
    loginLogoKey: z.string().nullish(),
    loginLogoUrl: z.string().max(2083).nullish(),
    primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format'),
    secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format'),
    faviconKey: z.string().nullish(),
    faviconUrl: z.string().max(2083).nullish(),
  }),
  portal: PortalSchema.optional(),
  accountSettings: AccountSettingSchema,
  filteredDomains: z.array(z.string()).optional(),
});

export type PortalSettingsDto = z.infer<typeof PortalSettingsSchema>;

export const PublishIdeaToPortalsSchema = z.object({
  portalIds: z.array(z.number()),
});

export type PublishIdeaToPortalsDto = z.infer<typeof PublishIdeaToPortalsSchema>;

export const IdeaPortalAssociationSchema = z.object({
  portalId: z.number(),
  portalName: z.string().nullish(),
  publishedAt: z.date(),
  publishedBy: z.string(),
});

export type IdeaPortalAssociationDto = z.infer<typeof IdeaPortalAssociationSchema>;

export const LandingPagePortalSchema = z.object({
  id: z.number(),
  name: z.string(),
  portalSlug: z.string(),
  loginLogoUrl: z.string().nullable(),
});

export const LandingPageResponseSchema = z.object({
  hasPortals: z.boolean(),
  organizationName: z.string(),
  defaultPortalSlug: z.string(),
  portals: z.array(LandingPagePortalSchema),
});

export type LandingPageResponseDto = z.infer<typeof LandingPageResponseSchema>;
